import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useAppDispatch, useAppSelector, useLockBodyScroll } from '@/hooks';
import { setRulesModalOpen } from '@/store/slices/uiSlice';
import { useOutsideClick } from '@/hooks/useOutsideClick';

export type RuleRowType = {
  text: string;
  danger?: boolean;
};

export type RuleSectionType = {
  title: string;
  rows: RuleRowType[];
};

export type SportRulesType = {
  sections: RuleSectionType[];
};

export const sportsRules: Record<string, SportRulesType> = {
  Football: {
    sections: [
      // ================= BOOKMAKER =================
      {
        title: 'Bookmaker',
        rows: [
          {
            text: 'If the match will not take place within 48 hours of the original kick-off time bets will be void.',
          },
          {
            text: 'If the selection is in a multiple bet or accumulator any refund must be requested before kick-off of the first leg of the multiple bet.',
            danger: true,
          },
          {
            text: 'Please note that games which have their kick-off altered well in advance to accommodate live TV, or to ease fixture congestion will not be classed as postponed.',
          },
          {
            text: 'If a match is forfeited or a team is given a walkover victory without the match having kicked off, then all bets will be void. Any subsequently awarded scoreline will not count for settlement purposes.',
            danger: true,
          },
          {
            text: 'Where a confirmed postponed match features as part of a multiple bet, the bet will stand on the remaining selections in the multiple.',
            danger: true,
          },
        ],
      },

      // ================= FANCY =================
      {
        title: 'Fancy',
        rows: [
          {
            text: 'Tournament Total Goals, Team Total Goals goals. scored in 90 minutes or in extra-time will count.Goals scored in penalty shootouts do not count.',
            danger: true,
          },
          {
            text: 'Tournament Corners - Only corners taken in 90 minutes count.',
            danger: true,
          },
          {
            text: 'Tournament Penalties Missed/Converted - Penalties taken in 90 minutes, extra-time and penalty shootouts all count. If a penalty has to be re-taken the previous disallowed penalty(ies) do not count.',
            danger: true,
          },
        ],
      },

      // ================= MATCH =================
      {
        title: 'Match',
        rows: [
          {
            text: "Match Odds :- All bets apply to the relevant full 'regular time' period including stoppage time. Any extra-time and/or penalty shoot-out is not included. For the cancellation of a goal, due to VAR, bets matched between the time of the goal being scored and the time at which the video assistant referee finishes the review will be voided. For the cancellation of a red card, due to VAR, bets matched after the time at which the video assistant referee commences the review will be voided.",
          },
          {
            text: 'Under_Over Goals :- In the event of a match starting but not being completed, all bets will be void, unless the specific market outcome is already determined.',
          },
          {
            text: '1st Period Winner :- Bets will be void if the match is abandoned before half-time.',
          },
          {
            text: 'Next Goal :- Own goals count to the side credited with the goal.',
          },
          {
            text: 'Draw No Bet :- Predict which team will be the winner. In case of a draw, all bets will be void. If a game is abandoned, bets will be void.',
          },
          {
            text: 'Both Teams to Score :- Predict whether both teams will score at least one goal in the game. Own goals count towards the team credited with the goal. If a game is abandoned, bets will be void, unless the outcome of these bets is already determined. Yes – meaning that both teams will score. No – meaning that either team will not score.',
          },
          {
            text: 'Total Corners :- Predict which team will take the named corner in the game. If this specific corner is not taken in the game, bets will be void. For example, game finishes or is abandoned with 8 corners taken – all bets on any corner after the 8th will be void (9th, 10th, etc.).',
          },
          {
            text: 'Goals Odd/Even :- Any match resulting in 0-0 will be settled on an even number of goals. For Team Odd/Even markets, if the specified team does not score then we will settle on an even number of goals. In the event of an abandoned match then bets for that match will be void.',
          },
          {
            text: '1X2 Corners :- Predict which team will get more corners in a match. Awarded, but not taken corners will not count for settlement purposes. Also if a corner needs to be re-taken for any reason, it will be counted as 1 corner.',
          },
          {
            text: 'Under/Over Card in Match (Number of cards) :- Predict the number of cards shown in a match. If a player is sent off for 2 yellow cards, this counts as 2 yellow cards and 1 red card. Cards for non-players are not considered. Cards after the full-time whistle are disregarded.',
          },
          {
            text: 'HT/FT :- Bets will be void if the match is abandoned. Extra-time and penalty shootouts do not count.',
          },
          {
            text: 'First Half Under/Over Goals :- All bets apply to Full Time plus stoppage time. Extra-time/penalty shoot-outs are not included.',
          },
          {
            text: 'Penalty Taken? :- Will a penalty be awarded and taken during this match? Applies to Full Time plus stoppage time only.',
          },
          {
            text: 'Correct Score :- Predict the score of this match. Settled at full time only. Extra-time/penalty shoot-outs are not included.',
          },
          {
            text: 'Corners Number/Odds :- How many corners will be taken in the match? Only taken corners count.',
          },
          {
            text: 'Team_A/B +1/2/3 :- Who will win this match with the stated handicap applied?',
          },
          {
            text: 'In the event of a technical error or unforeseen circumstance that leads to suspension of any market during a sporting event, settlement will be based on the current result at suspension. All bets remain valid.',
            danger: true,
          },
          {
            text: 'Company reserves the right to suspend/void any id/bets if found illegitimate (VPN/robot-use/multiple entry from same or different IP). Note: only winning bets will be voided.',
            danger: true,
          },
          {
            text: 'For live streaming and animation data may be delayed or inaccurate. Users rely on this data at their own risk. No warranty is provided regarding accuracy or timeliness.',
          },
        ],
      },
    ],
  },

  // ===== YOU WILL FILL THESE =====
  Cricket: {
    sections: [
      // ================= BOOKMAKER =================
      {
        title: 'Bookmaker',
        rows: [
          {
            text: '1. Due to any reason any team will be getting advantage or disadvantage we are not concerned.',
            danger: true,
          },
          {
            text: '4. Any query about the result or rates should be contacted within 7 days of the specific event, the same will not be considered valid post 7 days from the event.',
            danger: true,
          },
          {
            text: '5. Company reserves the right to suspend/void any id/bets if the same is found to be illegitimate. For example incase of vpn/robot-use/multiple entry from same IP/ multiple bets at the same time (Punching) and others. Note : only winning bets will be voided.',
            danger: true,
          },
          {
            text: '8. Super Over Bookmaker: If the scores are level at the end of both innings in the Super Over, the same market will continue into subsequent Super Overs until a winner is declared. (This rule applicable from 19th July 2026)',
            danger: true,
          },
          {
            text: '9. In case technical error or any circumstances any bookmaker is suspended and does not resume result will be given all previous bets will be valid (based on haar/jeet).',
            danger: true,
          },
          {
            text: '6. In case, company will find Ground bets, Group betting, Punching bets, Multiple entries with same IP or any fraud or unusual activities are detected then Company will be void winning bets and charge penalty of 2X (Two times) from winning amount.',
            danger: true,
          },
          {
            text: '7. If two team ends up with equal points, then result will be given based on the official point table',
            danger: true,
          },
          {
            text: '2. We will simply compare both teams 25 overs score higher score team will be declared winner in ODI (If both teams same score means, low wickets team will be declared winner. In case, both teams same score & same wickets means highest boundaries team will be declared winner.If all same then will be declared No result)',
            danger: true,
          },
          {
            text: '3. We will simply compare both teams 10 overs higher score team will be declared winner in T20 matches (If both teams same score means, low wickets team will be declared winner. In case, both teams same score & same wickets means highest boundaries team will be declared winner.If all same then will be declared No result)',
            danger: true,
          },
          {
            text: '10. If a Match, Series, or League is postponed or rescheduled before any ball is bowled, all bets for that event will be voided. However, if play has already started and the match continues on a reserve day, then all bets will remain valid.',
            danger: true,
          },
          {
            text: '11. If, for any reason, the trophy is shared between both teams, then all related bets will be voided.',
            danger: true,
          },
        ],
      },

      // ================= FANCY =================
      {
        title: 'Fancy',
        rows: [
          {
            text: '1. All fancy bets will be validated when match has been tied.',
            danger: true,
          },
          {
            text: '2. All advance fancy will be suspended before toss or weather condition. All advance fancy will be voided if over reduced before match start.',
            danger: true,
          },
          {
            text: '6. In case customer make bets in wrong fancy we are not liable to delete. No changes will be made and bets will be consider as confirm bet.',
            danger: true,
          },
          {
            text: '7. Due to any technical error market is open and result has came all bets after result will be delete',
            danger: true,
          },
          {
            text: '8. Manual bets are not accepted in our exchange.',
            danger: true,
          },
          {
            text: '9.Our exchange will provide 5 second delay in our TV.',
            danger: true,
          },
          {
            text: '10. Company reserves the right to suspend/void any id/bets if the same is found to be illegitimate. For example incase of VPN/robot-use/multiple entry from same IP and others. Note : only winning bets will be voided.',
            danger: true,
          },
          {
            text: '12. Once our exchange give username and password it is your responsibility to change a password.',
            danger: true,
          },
          {
            text: '13. Penalty runs will be counted in all fancy. (This rule applicable from 20th March 2024)',
            danger: true,
          },
          {
            text: '14. Warning:- live scores and other data on this site is sourced from third party feeds and may be subject to time delays and/or be inaccurate. If you rely on this data to place bets, you do so at your own risk. Our exchange does not accept responsibility for loss suffered as a result of reliance on this data.',
            danger: true,
          },
          {
            text: '15. Traders will be block the user ID if find any misinterpret activities, No queries accept regarding.',
            danger: true,
          },
        ],
      },

      // ================= MATCH =================
      {
        title: 'Match',
        rows: [
          {
            text: 'MIf IPL fixture of 74 matches gets reduced due to any reason, then all the special fancies will be voided (Match abandoned due to rain/bad light will not be considered in this)',
            danger: true,
          },
          {
            text: 'At any situation if result is given for any particular event based on the rates given for the same, then the particular result will be considered valid, similarly if the tournament gets canceled due to any reason the previously given result will be considered valid',
            danger: true,
          },
          {
            text: 'Total Run out:- Average 1 Run out will be given in case match abandoned or over reduced',
          },
          {
            text: 'Total Duckouts in IPL : Average 1 Duckout will be given in case match abandoned or over reduced',
          },
          {
            text: 'Total 50+ Partnerships - Average 2 Fifty plus Partnerships will be given in case match abandoned or over reduced. 50 and 50 Above Partnerships All Counted in this.',
          },
          {
            text: 'Total Highest Scoring Over Runs in IPL: Total of Every Match Highest Scoring Over Runs. Average 20 Runs will be given in case match abandoned or over reduced.',
          },
          {
            text: 'Total Runs Scored in IPL : Total Runs Scored in Full Tournament. Average 350 Runs will be Counted in case match abandoned or over reduced. Both Innings Counted.',
          },
          {
            text: 'Total match 1st over run:- Average 6 runs will be given if total 20 overs is not played, only 1st innings will be considered as valid',
          },
          {
            text: 'Highest innings run - Only first innings is valid',
          },
          {
            text: 'ULowest innings run - Only first innings is valid',
          },
          {
            text: 'Total 1st 6 over run:- Average 46 runs will be given if total 20 overs is not played, This event is valid only for the 1st innings',
          },
          {
            text: 'Total Fours - Average 25 fours will be given in case match abandoned or over reduced',
          },
          {
            text: 'Total Sixes - Average 10 sixes will be given in case match abandoned or over reduced',
          },
          {
            text: 'Total Wickets - Average will 12 Wickets be given in case match abandoned or over reduced',
          },
          {
            text: 'Total Wides - Average 8 wides will be given in case match abandoned or over reduced',
          },
          {
            text: 'Total Extras - Average 14 extras will be given in case match abandoned or over reduced',
          },
          {
            text: 'Total Fifties - Average 2 fifties will be given in case match abandoned or over reduced',
          },
          {
            text: 'All events related to bowler are valid only for the league stages, both the innings will be considered as valid. A minimum of 32 overs has to be bowled else the same will be voided. If the mentioned bowler has bowled one legal delivery then it will be considered as 1 over even if the over is not completed',
            danger: true,
          },
          {
            text: 'All events based on ground are decided based on the initial fixture, in case of any changes in the venue between the series. Then average will be given based on the initial fixture for the number of games reduced, Similarly if any match is added newly to the venue will not be considered',
            danger: true,
          },
          {
            text: 'Average for wickets taken will be given in case match abandoned or over reduced or the player has not bowled single legal delivery before the over got reduced',
            danger: true,
          },
          {
            text: 'Fancy based on all individual teams/players/ground are valid only for league stage',
            danger: true,
          },
          {
            text: 'Management decision will be final',
            danger: true,
          },
        ],
      },
    ],
  },
  Tennis: {
    sections: [
      {
        title: 'Match',
        rows: [
          {
            text: '“In the event of a technical error or any unforeseen circumstance that leads to the suspension of the Provider’s side of any market (Match_Odds,Over/Under, Game Winner, Point Winner, etc.) during a sporting event, and the market does not resume, the outcome will be settled based on the current result (win/loss) at the time of suspension. All bets placed prior to suspension will remain valid and will be settled accordingly. The company assumes no responsibility or liability for such interruptions or outcomes.”',
            danger: true,
          },
          {
            text: 'Company reserves the right to suspend/void any id/bets if the same is found to be illegitimate. For example incase of VPN/robot-use/multiple entry from same or different IP and others. Note : only winning bets will be voided.',
            danger: true,
          },
          {
            text: 'Match Odds :-If 1st set has been not completed at the time of the retirement or disqualification, then all bets relating to that individual match will be void.after completion of 1st set in case of retirement the opponent player will be given the winner for match winner bets.',
          },
          {
            text: 'Game Winner :- Predict which player will win the stated game. The nominated game will be featured in the name of the bet type, for example: 2ndset – 7 th game – Winner. If a game is not completed for any reason, bets on it will be void. Tie break points will not be counted for this bet type. unless the specific market outcome is already determined.',
          },
          {
            text: 'Under / Over Games :- Finished set stand, the unfinished set can be played to its natural conclusion and settled as in the example: Example: A set is abandoned at 4-4 I win if I placed a bet on Over 9.5 (since any natural conclusion to the set would have at least 10 games); I lost the bet if I placed a bet on Under 9.5 (since any natural conclusion to the set would have at least 10 games); I get my stake back if I placed a bet on O/U 10.5 (it is undecided, the set could have ended 6-4).',
          },
          {
            text: `
for live streaming and animation :- Although the current score, time elapsed, video and other data provided on this site is sourced from "live" feeds provided by third parties, you should be aware that this data may be subject to a time delay and/or be inaccurate. Please also be aware that other customers may have access to data that is faster and/or more accurate than the data shown on the site. If you rely on this data to place bets, you do so entirely at your own risk. provides this data AS IS with no warranty as to the accuracy, completeness or timeliness of such data and accepts no responsibility for any loss (direct or indirect) suffered by you as a result of your reliance on it.`,
          },
        ],
      },
    ],
  },

  // ===== OTHER SPORTS EMPTY =====
  'Horse Racing': { sections: [] },
  'E Games': { sections: [] },
  Basketball: { sections: [] },
  MotoGP: { sections: [] },
  Chess: { sections: [] },
  Volleyball: { sections: [] },
  'Ice Hockey': { sections: [] },
  Badminton: { sections: [] },
  Cycling: { sections: [] },
  'Mixed Martial Arts': { sections: [] },
  Motorbikes: { sections: [] },
  Athletics: { sections: [] },
  'Basketball 3X3': { sections: [] },
  Sumo: { sections: [] },
  'Virtual Sports': { sections: [] },
  Handball: { sections: [] },
  Politics: { sections: [] },
  Golf: { sections: [] },
  'Motor Sports': { sections: [] },
  Baseball: { sections: [] },
  'Rugby Union': { sections: [] },
  'Rugby League': { sections: [] },
  Darts: { sections: [] },
  'American Football': { sections: [] },
  Snooker: { sections: [] },
  Boxing: { sections: [] },
  Soccer: { sections: [] },
  Esports: { sections: [] },
  'Greyhound Racing': { sections: [] },
  Kabaddi: { sections: [] },
  'Boat Racing': { sections: [] },
  Esoccer: { sections: [] },
  'Beach Volleyball': { sections: [] },
  'Table Tennis': { sections: [] },
  Futsal: { sections: [] },
};

const sports = [
  'Football',
  'Horse Racing',
  'E Games',
  'Basketball',
  'MotoGP',
  'Chess',
  'Volleyball',
  'Ice Hockey',
  'Tennis',
  'Badminton',
  'Cycling',
  'Mixed Martial Arts',
  'Motorbikes',
  'Athletics',
  'Basketball 3X3',
  'Sumo',
  'Virtual Sports',
  'Handball',
  'Cricket',
  'Politics',
  'Golf',
  'Motor Sports',
  'Baseball',
  'Rugby Union',
  'Rugby League',
  'Darts',
  'American Football',
  'Snooker',
  'Boxing',
  'Soccer',
  'Esports',
  'Greyhound Racing',
  'Kabaddi',
  'Boat Racing',
  'Esoccer',
  'Beach Volleyball',
  'Table Tennis',
  'Futsal',
];

// ================= SECTION =================
const RuleSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <div className="bg-secondary xl:bg-black text-white px-3 py-2 font-bold">{title}</div>

    <div className="p-[10px]">{children}</div>
  </div>
);

// ================= ROW =================
const RuleRow = ({ text, danger }: { text: string; danger?: boolean }) => (
  <div className="bg-neutral-100 px-3 py-2 text-base border-x border-t border-neutral-700 last:border-b">
    <p className={danger ? 'text-primary' : 'text-gray-800'}>{text}</p>
  </div>
);

// ================= MODAL =================
const RulesModal: React.FC = () => {
  const [selectedSport, setSelectedSport] = useState('Football');
  const { rulesModalOpen } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();

  useLockBodyScroll(rulesModalOpen);
  const modalRef = useOutsideClick<HTMLDivElement>(() => {
    dispatch(setRulesModalOpen(false));
  }, rulesModalOpen);

  if (!rulesModalOpen) return null;

  const currentRules = sportsRules[selectedSport];

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60 overflow-y-auto px-3 md:px-6 pt-[1vh]">
      <div
        ref={modalRef}
        className="w-full max-w-[1140px] bg-white shadow-xl flex flex-col transform transition-all duration-300 ease-out animate-slide-in-from-top"
      >
        {/* ================= HEADER ================= */}
        <div className="bg-primary text-white px-6 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold">Rules</h2>

          <div className="flex items-center gap-4">
            <div className="bg-gray-200 text-black px-3 py-1 text-sm">🇬🇧 English</div>

            <button onClick={() => dispatch(setRulesModalOpen(false))} className="text-xl">
              ✕
            </button>
          </div>
        </div>

        {/* ================= MOBILE SPORTS (Swiper) ================= */}
        <div className="md:hidden border-b border-secondary bg-neutral-400 mt-1">
          <Swiper slidesPerView="auto" spaceBetween={6}>
            {sports.map((sport) => (
              <SwiperSlide key={sport} className="!w-auto">
                <button
                  onClick={() => setSelectedSport(sport)}
                  className={`px-3 py-2 text-sm whitespace-nowrap
                    ${selectedSport === sport ? 'bg-primary text-white' : ''}`}
                >
                  {sport}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* ================= BODY ================= */}
        <div className="flex min-h-[600px] max-h-[75vh] p-1.5 xl:p-3.5">
          {/* ================= DESKTOP SIDEBAR ================= */}
          <aside className="hidden md:block w-[230px] border-r overflow-y-auto">
            {sports.map((sport, index) => (
              <div
                key={sport}
                onClick={() => setSelectedSport(sport)}
                className={`px-4 py-2 text-sm border border-collapse border-black cursor-pointer ${index !== 0 ? 'border-t-0' : ''}
                ${
                  selectedSport === sport ? 'bg-primary text-white font-medium' : 'bg-neutral-400'
                }`}
              >
                {sport}
              </div>
            ))}
          </aside>

          {/* ================= CONTENT ================= */}
          <main className="flex-1 overflow-y-auto space-y-6">
            {currentRules?.sections?.length ? (
              currentRules.sections.map((section) => (
                <RuleSection key={section.title} title={section.title}>
                  {section.rows.map((row, index) => (
                    <RuleRow key={index} text={row.text} danger={row.danger} />
                  ))}
                </RuleSection>
              ))
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                Rules not available for this sport yet.
              </div>
            )}
          </main>
        </div>

        {/* ================= FOOTER ================= */}
        <div className="bg-neutral-100 border-t flex justify-end px-6 py-3">
          <button
            onClick={() => dispatch(setRulesModalOpen(false))}
            className="bg-red-700 hover:bg-red-800 text-white px-6 py-2 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RulesModal;
